<template>
  <modal :show="showModal" modal-classes="modal-secondary" size="lg">
    <div class="text-center mb-4">
      <h2>{{ $t("SALES_ORDERS.ADD_COUPONS_SALES_ORDER") }}</h2>
    </div>

    <div class="row">
      <div class="col-12">
        <base-input
          :label="`${$t('SALES_ORDERS.COUPONS_ITEM_NAME')} (*)`"
          :placeholder="$t('SALES_ORDERS.COUPONS_ITEM_NAME')"
          v-model="item_name"
        >
        </base-input>
      </div>
    </div>

    <h4 class="mt-2">{{ $t("SALES_ORDERS.COUPONS_BILLING_PERIOD") }} (*)</h4>
    <div class="row">
      <div class="col-6">
        <base-input :label="$t('COMMON.START_DATE')">
          <flat-picker
            :config="{ allowInput: true, locale: $flatPickrLocale() }"
            class="form-control datepicker"
            v-model="period_start"
          >
          </flat-picker>
        </base-input>
      </div>
      <div class="col-6">
        <base-input :label="$t('COMMON.END_DATE')">
          <flat-picker
            :config="{ allowInput: true, locale: $flatPickrLocale() }"
            class="form-control datepicker"
            v-model="period_end"
            @on-change="refreshBalance"
          >
          </flat-picker>
        </base-input>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-12">
        <base-input
          :label="`${$t('SALES_ORDERS.COUPONS_AMOUNT')} (*)`"
          :placeholder="$t('SALES_ORDERS.COUPONS_AMOUNT')"
          v-model="amount"
          type="number"
          step="0.01"
          input-classes="form-control-alternative"
        >
        </base-input>
        <small class="text-muted d-block" v-if="!balanceLoading">
          {{
            $t("SALES_ORDERS.COUPONS_REMAINING_HINT", {
              amount: $formatCurrency(remainingToInvoice),
            })
          }}
        </small>
        <small class="text-muted d-block" v-else>
          <i class="fas fa-spinner fa-spin"></i>
        </small>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-12">
        <base-input :label="`${$t('SALES_ORDERS.SELECT_CUSTOMERS')}`">
          <customer-selector
            :allowNone="false"
            :customer="selectedRecipientId"
            :filterable="true"
            :showAll="false"
            :filterOrganization="establishment.organization.id"
            :bypassScope="true"
            @customerChanged="onRecipientChanged"
          />
        </base-input>
      </div>
    </div>

    <template slot="footer">
      <button
        type="submit"
        class="btn btn-primary"
        @click="addCouponsSalesOrder"
        :disabled="loading"
      >
        <span v-if="loading" class="btn-inner--icon">
          <i class="fas fa-spinner fa-spin"></i>
        </span>
        {{ $t("SALES_ORDERS.ADD_COUPONS_SALES_ORDER") }}
      </button>

      <button
        type="button"
        class="btn btn-link ml-auto"
        @click="closeModal"
        :disabled="loading"
      >
        {{ $t("COMMON.CLOSE") }}
      </button>
    </template>
  </modal>
</template>

<script>
import moment from "moment";
import swal from "sweetalert2";
import { mapGetters } from "vuex";
import { cloneDeep } from "lodash";
import flatPicker from "vue-flatpickr-component";
import formMixin from "@/mixins/form-mixin";
import CustomerSelector from "@/components/CustomerSelector.vue";
import defaultSalesOrder from "../../../SalesModule/SalesOrderManagement/defaultSalesOrder";
import defaultSalesOrderItem from "../../../SalesModule/SalesOrderManagement/defaultSalesOrderItem";
import { PRICING_TYPE_COUPONS } from "../../../../../constants/pricingTypes";

export default {
  name: "establishment-view-sales-orders-add-coupons-sales-order-modal",

  components: { flatPicker, CustomerSelector },

  mixins: [formMixin],

  props: ["establishment", "showModal"],

  data() {
    return {
      loading: false,
      balanceLoading: false,
      item_name: null,
      period_start: null,
      period_end: null,
      amount: 0,
      remainingToInvoice: 0,
      selectedRecipientId: null,
      selectedRecipient: null,
    };
  },

  computed: {
    ...mapGetters({
      organizationConfig: "organizationOptions/organizationConfig",
    }),

    defaultRecipient() {
      return (
        (this.establishment &&
          this.establishment.organization &&
          this.establishment.organization.customer) ||
        this.establishment.customer ||
        null
      );
    },
  },

  created() {
    this.setDefaultRecipient();
  },

  methods: {
    onRecipientChanged(customerId, customer) {
      this.selectedRecipientId = customerId;
      this.selectedRecipient = customer || null;
    },

    setDefaultRecipient() {
      if (this.defaultRecipient && this.defaultRecipient.id) {
        this.selectedRecipientId = this.defaultRecipient.id;
        this.selectedRecipient = this.defaultRecipient;
      } else {
        this.selectedRecipientId = null;
        this.selectedRecipient = null;
      }
    },

    prefillForm() {
      this.item_name = this.$t("SALES_ORDERS.PRICING_TYPE_COUPONS");
      this.period_start = moment().startOf("month").format("YYYY-MM-DD");
      this.period_end = moment().format("YYYY-MM-DD");
      this.setDefaultRecipient();
    },

    async loadOrganizationConfig() {
      if (!this.establishment || !this.establishment.organization) {
        return;
      }
      await this.$store.dispatch(
        "organizationOptions/getOrganizationConfig",
        this.establishment.organization.id
      );
    },

    async refreshBalance() {
      if (!this.establishment || !this.establishment.id) {
        return;
      }

      this.balanceLoading = true;
      try {
        const balance = await this.$store.dispatch(
          "establishments/couponsBalance",
          {
            id: this.establishment.id,
            asOf: this.period_end
              ? moment(this.period_end).format("YYYY-MM-DD")
              : null,
          }
        );
        this.remainingToInvoice = Number(balance.remaining_to_invoice || 0);
        this.amount = this.remainingToInvoice;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
      } finally {
        this.balanceLoading = false;
      }
    },

    itemDescription() {
      return this.$t("SALES_ORDERS.COUPONS_ITEM_DESCRIPTION", {
        start: moment(this.period_start).format("LL"),
        end: moment(this.period_end).format("LL"),
      });
    },

    async addCouponsSalesOrder() {
      if (!this.item_name) {
        this.$notify({
          type: "danger",
          message: `${this.$t("SALES_ORDERS.COUPONS_ITEM_NAME")} ${this.$t(
            "ERRORS.IS_REQUIRED"
          )}`,
        });
        return;
      }

      if (!this.period_start || !this.period_end) {
        this.$notify({
          type: "danger",
          message: `${this.$t("SALES_ORDERS.COUPONS_BILLING_PERIOD")} ${this.$t(
            "ERRORS.IS_REQUIRED"
          )}`,
        });
        return;
      }

      swal.fire({
        allowEscapeKey: false,
        allowOutsideClick: false,
        onOpen: () => {
          swal.showLoading();
        },
      });

      this.loading = true;
      await this.$store.dispatch(
        "organizationOptions/getOrganizationConfig",
        this.establishment.organization.id
      );

      const salesOrderData = cloneDeep(defaultSalesOrder);
      delete salesOrderData.salesInvoice;
      salesOrderData.expiration_time = moment(
        salesOrderData.expiration_time
      ).toISOString();

      const recipient = this.selectedRecipient || this.defaultRecipient || null;
      const recipientId =
        this.selectedRecipientId ||
        (recipient && recipient.id ? recipient.id : null);

      // Les coupons à facturer ne sont jamais taxés (argent déjà comptabilisé aux caisses).
      salesOrderData.has_no_taxes = true;
      salesOrderData.pricing_type = PRICING_TYPE_COUPONS;
      salesOrderData.recipient = { type: "customers", id: recipientId };
      salesOrderData.organization = this.establishment.organization;
      salesOrderData.allowedLocations = this.establishment.allowedLocations;
      salesOrderData.billing_entity_type =
        (recipient && recipient.customer_type) ||
        this.establishment.billing_entity_type;
      salesOrderData.billing_company_name =
        (recipient && recipient.company_name) ||
        this.establishment.billing_company_name;
      salesOrderData.billing_firstname =
        (recipient && recipient.firstname) ||
        this.establishment.billing_firstname;
      salesOrderData.billing_lastname =
        (recipient && recipient.lastname) ||
        this.establishment.billing_lastname;
      salesOrderData.billing_country =
        (recipient && recipient.country) || this.establishment.billing_country;
      salesOrderData.billing_state =
        (recipient && recipient.state) || this.establishment.billing_state;
      salesOrderData.billing_city =
        (recipient && recipient.city) || this.establishment.billing_city;
      salesOrderData.billing_zipcode =
        (recipient && recipient.zipcode) || this.establishment.billing_zipcode;
      salesOrderData.billing_address =
        (recipient && recipient.address) || this.establishment.billing_address;
      salesOrderData.billing_email =
        (recipient && recipient.email) || this.establishment.billing_email;
      salesOrderData.terms_conditions =
        this.organizationConfig.DEFAULT_SALES_ORDER_TERMS;
      salesOrderData.description =
        this.organizationConfig.DEFAULT_SALES_ORDER_DESCRIPTION;
      salesOrderData.paymentTerm = {
        type: "payment-terms",
        id: this.organizationConfig.DEFAULT_SALES_PAYMENT_TERMS,
      };

      delete salesOrderData.cafeteria;
      salesOrderData.establishment = {
        type: "establishments",
        id: this.establishment.id,
      };
      salesOrderData.serviceCenter = {
        type: "service-centers",
        id: this.establishment.serviceCenter.id,
      };
      if (this.establishment.has_warehouse) {
        salesOrderData.sourceWarehouse = {
          type: "warehouses",
          id: this.establishment.warehouse.id,
        };
      } else {
        delete salesOrderData.sourceWarehouse;
      }

      // La période facturée tient lieu d'événement : le nom porte l'item, la
      // date de fin sert de date de référence pour la facturation.
      salesOrderData.event_name = this.item_name;
      salesOrderData.event_date = moment(this.period_end)
        .endOf("day")
        .toISOString();

      try {
        await this.$store.dispatch("salesOrders/add", salesOrderData);
        const salesOrder = await this.$store.getters["salesOrders/salesOrder"];

        // Ligne libre : aucun produit du catalogue derrière ce montant.
        const salesOrderItemData = cloneDeep(defaultSalesOrderItem);
        delete salesOrderItemData.salesOrderable;
        delete salesOrderItemData.unitOfMeasureUnit;
        salesOrderItemData.relationshipNames = ["salesOrder"];
        delete salesOrderItemData.taxGroups;
        salesOrderItemData.salesOrder = {
          type: "sales-orders",
          id: salesOrder.id,
        };
        salesOrderItemData.code = this.item_name;
        salesOrderItemData.excerpt = this.itemDescription();
        salesOrderItemData.quantity = 1;
        salesOrderItemData.unit_price = Number(this.amount || 0);

        await this.$store.dispatch("salesOrderItems/add", salesOrderItemData);

        this.$notify({
          type: "success",
          message: this.$t("SALES_ORDERS.COUPONS_SALES_ORDER_ADDED"),
        });

        this.$router.push({
          name: "View SalesOrder",
          params: { id: salesOrder.id },
        });
        this.loading = false;
      } catch (error) {
        this.$notify({
          type: "danger",
          message: this.$t("ERRORS.SOMETHING_WENT_WRONG"),
        });
        this.formErrors = error.response?.data?.errors;
        this.loading = false;
      }
      swal.close();
    },

    closeModal() {
      this.$emit("onCloseModal");
    },
  },

  watch: {
    establishment: {
      immediate: true,
      handler() {
        this.setDefaultRecipient();
      },
    },
    showModal: {
      immediate: true,
      handler(showModal) {
        if (showModal) {
          this.prefillForm();
          this.loadOrganizationConfig();
          this.refreshBalance();
        }
      },
    },
  },
};
</script>
